/**
 * BudgetApp - Main Application Entry Point
 * Production-ready modular structure
 */

// Import styles
import '../css/main.css';

// Import core modules
import { initializeAuth, setupAuthEventListeners, signOut, getCurrentUser, showPasswordResetModal, closePasswordResetModal, handlePasswordResetSubmit } from './modules/auth.js';
import { 
  initializeBudget, 
  calculateTotals, 
  saveCurrentBudget,
  saveCurrentVersion, 
  loadBudgetVersion, 
  renameCurrentVersion,
  initializeBudgetNameEditing,
  initializeInlineEditing,
  toggleVersionsPanel,
  handleBudgetManagerBackdropClick,
  filterBudgetVersions,
  setDefaultVersion,
  toggleDefaultVersion,
  duplicateVersion,
  deleteVersion,
  addIncomeItem,
  addExpenseCategory,
  addItemToCategory,
  removeItem,
  removeCategory,
  importBudgetFromFile,
  captureBudgetData,
  restoreBudgetData,
  createBudgetSnapshot,
  toggleRecurringPanel,
  saveRecurringConfig,
  cancelRecurringConfig,
  getRecurringExpenses,
  generateAllRecurringExpenses,
  getCurrentMonthRecurringExpenses,
  getUpcomingRecurringExpenses,
  testRecurringGeneration,
  initializeRecurringCalendar,
  navigateCalendar,
  renderCalendar,
  getCurrentBudgetData,
  initializeAutoLogging,
  processRecurringExpenses,
  manualProcessRecurringExpenses,
  getRecurringExpenseStatus,
  getCurrentBudgetId,
  getCurrentVersionName,
  getBudgetVersions,
  openTargetModal,
  closeTargetModal,
  saveTargetFromModal,
  updateModalInputRow,
  toggleIncomeFrequencyPanel,
  saveIncomeFrequencyConfig,
  cancelIncomeFrequencyConfig,
  toggleIncomeTaxPanel,
  saveIncomeTaxConfig,
  cancelIncomeTaxConfig,
  toggleIncomeTimingPanel,
  saveIncomeTimingConfig,
  cancelIncomeTimingConfig
} from './modules/budget.js';
import { initializeMobileSupport, toggleMenu, closeMenu } from './modules/utils/mobile.js';
import { startPageTour } from './modules/pageTour.js';
import { setupCurrencyInput } from './modules/utils/currency.js';
import { initializeIncomeManagement } from './modules/incomeManagement.js';
import { initializeIncomeWizardUI, openIncomeWizard, closeIncomeWizard } from './modules/incomeWizardUI.js';
import { 
  setupLinkSharingEventListeners, 
  showLinkCodeModal, 
  showLinkRedemptionModal,
  updateLinkStatusIndicator,
  setLinkingState,
  validateCodeInputs,
  checkForFollowers,
  handleShareAccessClick,
  updateShareAccessButton,
  unlinkHousehold
} from './modules/linkSharing.js';
import { 
  initializeExpenseTracker,
  updateOverallProgress,
  deleteExpense,
  initializeExpenseTrackerHistory,
  editExpenseFromTrackerHistory,
  deleteExpenseFromTrackerHistory,
  saveExpenseEdit,
  cancelExpenseEdit,
  clearExpenseTrackerHistoryFilter,
  filterExpenseTrackerHistoryByCategory,
  insertAmountOperator,
  deleteAmountChar
} from './modules/expenseTracker.js';
import {
  initializeVisualizations,
  renderSankeyDiagram,
  resizeChart,
  renderBudgetVsActualChart,
  initializeHistoricalTrends,
  initializeSpendingComparison,
  switchBarChartMode,
  updateFlowSummaryCards
} from './modules/visualizations.js';
import { initializeSettings, switchToSettings, getDefaultView } from './modules/settings.js';
import { updateComparison, refreshComparison } from './modules/comparison.js';
import achievementEngine from './modules/achievements.js';
import achievementUI, { initializeAchievementsView } from './modules/achievementUI.js';
import { initializeServiceWorker, getServiceWorkerStatus } from './modules/serviceWorker.js';
import { generateBreakdownSummary, formatCurrency as breakdownFormatCurrency } from './modules/budgleyBreakdown.js';
import { redirectToCheckout, openBillingPortal } from './modules/subscription.js';
import {
  initializeBankTrueUp, openBankTrueUpModal, closeBankTrueUpModal,
  bankTrueUpNextStep, bankTrueUpPrevStep, bankTrueUpAddTransaction, showTrueUpTab,
  resumeBankTrueUpModal
} from './modules/bankTrueUp.js';
import {
  initializeGettingStarted,
  markGettingStartedStep,
  closeGettingStarted,
  dismissGettingStarted,
  showGettingStarted,
  resetGettingStarted,
  setPendingSpotlight,
  applyPendingSpotlight,
} from './modules/gettingStarted.js';

/**
 * Application State
 */
let isCompareMode = false;
let isSankeyMode = false;
let isProgressViewMode = false;
let isRecurringCalendarMode = false;
let isExpenseTrackerMode = false;
let isSettingsMode = false;
let isAchievementsMode = false;
let isBreakdownMode = false;

/**
 * Hide iOS Safari address bar on page load
 */
function hideIOSAddressBar() {
  // Check if we're on iOS Safari
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
  
  if (isIOS && isSafari) {
    // Use setTimeout to ensure the page is fully loaded
    setTimeout(() => {
      // Scroll down slightly to hide the address bar
      window.scrollTo(0, 1);
      
      // Then scroll back to top
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    }, 100);
    
    // Also trigger on orientation change
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        window.scrollTo(0, 1);
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 100);
      }, 500);
    });
  }
}

/**
 * Ensure styles are loaded before showing content
 */
function ensureStylesLoaded() {
  return new Promise((resolve) => {
    // Check if styles are already loaded
    if (document.readyState === 'complete') {
      document.documentElement.classList.add('styles-loaded');
      resolve();
      return;
    }
    
    // Wait for page load
    const onLoad = () => {
      document.documentElement.classList.add('styles-loaded');
      window.removeEventListener('load', onLoad);
      resolve();
    };
    
    window.addEventListener('load', onLoad);
    
    // Fallback timeout
    setTimeout(() => {
      document.documentElement.classList.add('styles-loaded');
      resolve();
    }, 1000);
  });
}

/**
 * Check for month change and create budget snapshot if needed
 */
async function checkAndCreateMonthlySnapshot() {
  try {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    // Get last snapshot month from localStorage
    const lastSnapshotMonth = localStorage.getItem('lastBudgetSnapshotMonth');

    if (!lastSnapshotMonth || lastSnapshotMonth !== currentMonth) {
      console.log(`📸 Month changed from ${lastSnapshotMonth} to ${currentMonth}`);

      // If we have a previous month, create a snapshot for it
      if (lastSnapshotMonth) {
        const budgetData = captureBudgetData();
        await createBudgetSnapshot(budgetData, lastSnapshotMonth);
        console.log(`✅ Created snapshot for previous month: ${lastSnapshotMonth}`);
      }

      // Update stored month
      localStorage.setItem('lastBudgetSnapshotMonth', currentMonth);

      // Create snapshot for current month as baseline
      const budgetData = captureBudgetData();
      await createBudgetSnapshot(budgetData, currentMonth);
      console.log(`✅ Created snapshot for current month: ${currentMonth}`);
    }
  } catch (error) {
    console.error('Error in month change detection:', error);
    // Don't throw - this shouldn't block app initialization
  }
}

/**
 * Initialize the application
 */
async function initializeApp() {
  
  try {
    // TEMPORARILY DISABLED - Service worker causes issues during development
    // Initialize service worker first for PWA functionality
    // const swInitialized = await initializeServiceWorker();
    // console.log('Service Worker initialized:', swInitialized);
    const swInitialized = false;
    console.log('Service Worker DISABLED during development');
    
    // Ensure styles are loaded to prevent FOUC
    await ensureStylesLoaded();
    
    // Hide iOS Safari address bar
    hideIOSAddressBar();
    
    // Initialize mobile support
    initializeMobileSupport();
    
    // Setup authentication event listeners
    setupAuthEventListeners();
    
    // Setup link sharing event listeners
    setupLinkSharingEventListeners();
    
    // Initialize authentication state observer
    initializeAuth(async (user) => {
      if (user) {
        
        // Clear expense cache on user authentication to ensure fresh data
        if (typeof window !== 'undefined' && window.clearExpenseCache) {
          window.clearExpenseCache();
        }
        
        await initializeBudget(user);

        // Check for month change and create budget snapshot
        await checkAndCreateMonthlySnapshot();

        await initializeIncomeManagement();
        
        await initializeSettings();

        // Initialize getting-started checklist (no-ops for existing users)
        await initializeGettingStarted(user.uid);

        // Set default view after settings are loaded, with a small delay to ensure settings are populated
        setTimeout(() => setDefaultViewOnStartup(), 100);
        
        setupBudgetEventListeners();
        setupViewSwitchingEventListeners();
        setupMenuEventListeners();
        initializeIncomeWizardUI();
        
        // Initialize auto-logging for recurring expenses
        initializeAutoLogging();

        // Initialize bank true-up module
        initializeBankTrueUp();

        // Initialize achievement system
        await achievementEngine.initialize(user);
        await achievementUI.initialize();
        
        // Ensure floating button is properly initialized
        setTimeout(() => updateFloatingButton(), 100);
        
      }
    });
    
    // Setup global event listeners
    setupGlobalEventListeners();
    
    console.log('BudgetApp initialized successfully');
    
  } catch (error) {
    console.error('Failed to initialize app:', error);
  }
}

/**
 * Setup budget-related event listeners
 */
function setupBudgetEventListeners() {
  // Setup currency inputs
  const incomeInputs = document.querySelectorAll('.income-input');
  const expenseInputs = document.querySelectorAll('.expense-input');
  
  [...incomeInputs, ...expenseInputs].forEach(input => {
    setupCurrencyInput(input, calculateTotals);
  });
  
  // Version management buttons
  const saveBtn = document.querySelector('[onclick="saveCurrentVersion()"]');
  if (saveBtn) {
    saveBtn.onclick = saveCurrentVersion;
  }
}

/**
 * Setup view switching event listeners
 */
function setupViewSwitchingEventListeners() {
  // Budget view
  const budgetViewBtn = document.getElementById('single-view-btn');
  if (budgetViewBtn) {
    budgetViewBtn.addEventListener('click', switchToSingleView);
  }
  
  // Compare view
  const compareViewBtn = document.getElementById('compare-view-btn');
  if (compareViewBtn) {
    compareViewBtn.addEventListener('click', switchToCompareView);
  }
  
  // Sankey view
  const sankeyViewBtn = document.getElementById('sankey-view-btn');
  if (sankeyViewBtn) {
    sankeyViewBtn.addEventListener('click', switchToSankeyView);
  }
  
  // Progress view
  const progressViewBtn = document.getElementById('progress-view-btn');
  if (progressViewBtn) {
    progressViewBtn.addEventListener('click', switchToProgressView);
  }
  
  // Recurring calendar
  const recurringCalendarBtn = document.getElementById('recurring-calendar-btn');
  if (recurringCalendarBtn) {
    recurringCalendarBtn.addEventListener('click', switchToRecurringCalendar);
  }
  
  // Expense tracker
  const expenseTrackerBtn = document.getElementById('expense-tracker-btn');
  if (expenseTrackerBtn) {
    expenseTrackerBtn.addEventListener('click', switchToExpenseTracker);
  }
  
  // Achievements
  const achievementsBtn = document.getElementById('achievements-btn');
  if (achievementsBtn) {
    achievementsBtn.addEventListener('click', switchToAchievements);
  }
  
  // Settings
  const settingsBtn = document.getElementById('settings-btn');
  if (settingsBtn) {
    settingsBtn.addEventListener('click', switchToSettingsView);
  }
}

/**
 * Setup menu event listeners
 */
function setupMenuEventListeners() {
  // Menu toggle - using onclick in HTML
  // Menu overlay - using onclick in HTML
  
  // Note: Other menu buttons use onclick handlers in HTML for simplicity
  // This ensures they work even if JavaScript modules load after DOM
}

/**
 * Setup global event listeners
 */
function setupGlobalEventListeners() {
  // Close modals on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      // Close any open modals
      const modals = document.querySelectorAll('.modal, .link-code-modal, .link-redemption-modal, .bank-true-up-modal');
      modals.forEach(modal => {
        if (modal.classList.contains('active')) {
          modal.classList.remove('active');
          document.body.style.overflow = '';
        }
      });
      
      // Close target modals
      const targetModals = document.querySelectorAll('.target-modal.active');
      targetModals.forEach(modal => modal.classList.remove('active'));

      // Close budget manager modal
      const budgetManagerModal = document.getElementById('budget-manager-modal');
      if (budgetManagerModal && budgetManagerModal.style.display !== 'none') {
        budgetManagerModal.style.display = 'none';
      }
    }
  });
  
  // Close target modals when clicking outside
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.target-modal') && !e.target.closest('.target-icon')) {
      const openModals = document.querySelectorAll('.target-modal.active');
      openModals.forEach(modal => modal.classList.remove('active'));
    }
  });
  
  // Setup logo click handlers for navigation to home
  const mainLogo = document.querySelector('.header-logo');
  const stickyLogo = document.querySelector('.sticky-logo');
  
  if (mainLogo) {
    mainLogo.addEventListener('click', () => {
      console.log('Main logo clicked');
      navigateToHome();
    });
    mainLogo.style.cursor = 'pointer';
  }
  
  if (stickyLogo) {
    stickyLogo.addEventListener('click', () => {
      console.log('Sticky logo clicked');
      navigateToHome();
    });
    stickyLogo.style.cursor = 'pointer';
  }
  
  // Setup sticky header scroll behavior
  setupStickyHeader();
}

/**
 * Setup sticky header functionality
 */
function setupStickyHeader() {
  const stickyHeader = document.getElementById('sticky-header');
  const mainHeader = document.querySelector('.header');
  
  if (!stickyHeader || !mainHeader) return;
  
  let lastScrollY = window.scrollY;
  let ticking = false;
  
  function updateStickyHeader() {
    const scrollY = window.scrollY;
    const headerHeight = mainHeader.offsetHeight;
    
    // Show sticky header when scrolled past main header
    if (scrollY > headerHeight && scrollY > lastScrollY) {
      // Scrolling down past header
      stickyHeader.classList.add('visible');
    } else if (scrollY <= headerHeight) {
      // Back to top area
      stickyHeader.classList.remove('visible');
    }
    
    lastScrollY = scrollY;
    ticking = false;
    
    // Sync the status indicators
    syncStatusIndicators();
  }
  
  function requestTick() {
    if (!ticking) {
      requestAnimationFrame(updateStickyHeader);
      ticking = true;
    }
  }
  
  // Listen for scroll events
  window.addEventListener('scroll', requestTick, { passive: true });
  
  // Setup sticky menu toggle click handler (in case onclick doesn't work)
  const stickyMenuToggle = document.getElementById('sticky-menu-toggle');
  if (stickyMenuToggle) {
    stickyMenuToggle.addEventListener('click', (e) => {
      console.log('Sticky menu toggle clicked');
      console.log('Menu elements check:');
      console.log('- headerControls:', document.getElementById('header-controls'));
      console.log('- overlay:', document.getElementById('menu-overlay'));
      console.log('- toggleMenu function:', typeof toggleMenu);
      e.preventDefault();
      e.stopPropagation();
      toggleMenu();
    });
  } else {
    console.log('Sticky menu toggle not found during setup');
  }
  
  // Initial sync
  syncStatusIndicators();
}

/**
 * Sync status indicators between main and sticky headers
 */
function syncStatusIndicators() {
  const mainIndicator = document.getElementById('sync-indicator');
  const stickyIndicator = document.getElementById('sticky-sync-indicator');
  
  if (mainIndicator && stickyIndicator) {
    // Copy all classes from main to sticky
    stickyIndicator.className = mainIndicator.className.replace('sync-indicator', 'sticky-sync-indicator');
  }
  
  // Sync menu toggle states
  const mainMenuToggle = document.getElementById('menu-toggle');
  const stickyMenuToggle = document.getElementById('sticky-menu-toggle');
  
  if (mainMenuToggle && stickyMenuToggle) {
    if (mainMenuToggle.classList.contains('active')) {
      stickyMenuToggle.classList.add('active');
    } else {
      stickyMenuToggle.classList.remove('active');
    }
  }
}

/**
 * View switching functions
 */
function switchToSingleView() {
  closeMenu(); // Close menu when view is selected
  window.scrollTo(0, 0); // Reset scroll position
  hideAllViews();
  showView('single-mode');
  setActiveViewButton('single-view-btn');
  isCompareMode = false;
  isSankeyMode = false;
  isProgressViewMode = false;
  isRecurringCalendarMode = false;
  isExpenseTrackerMode = false;
  isAchievementsMode = false;
  updateFloatingButton();
  applyPendingSpotlight();
}

function switchToCompareView() {
  closeMenu(); // Close menu when view is selected
  window.scrollTo(0, 0); // Reset scroll position
  hideAllViews();
  showView('compare-mode');
  setActiveViewButton('compare-view-btn');
  isCompareMode = true;
  isSankeyMode = false;
  isProgressViewMode = false;
  isRecurringCalendarMode = false;
  isExpenseTrackerMode = false;
  isAchievementsMode = false;
  updateFloatingButton();
  
  // Initialize compare view
  updateComparisonView();
}

function switchToSankeyView() {
  closeMenu(); // Close menu when view is selected
  window.scrollTo(0, 0); // Reset scroll position
  hideAllViews();
  showView('sankey-mode');
  setActiveViewButton('sankey-view-btn');
  isCompareMode = false;
  isSankeyMode = true;
  isProgressViewMode = false;
  isRecurringCalendarMode = false;
  isExpenseTrackerMode = false;
  isAchievementsMode = false;
  updateFloatingButton();
  
  // Mark getting started step
  markGettingStartedStep('checkInsights');
  applyPendingSpotlight();

  // Initialize Sankey view
  renderSankeyChart();
  
  // Setup balance input after a short delay to ensure DOM is ready
  setTimeout(() => {
    if (typeof setupBalanceInput === 'function') {
      setupBalanceInput();
    }
  }, 100);
}

function switchToProgressView() {
  closeMenu(); // Close menu when view is selected
  window.scrollTo(0, 0); // Reset scroll position
  hideAllViews();
  showView('progress-view-mode');
  setActiveViewButton('progress-view-btn');
  isCompareMode = false;
  isSankeyMode = false;
  isProgressViewMode = true;
  isRecurringCalendarMode = false;
  isExpenseTrackerMode = false;
  isAchievementsMode = false;
  updateFloatingButton();
  
  // Mark getting started step
  markGettingStartedStep('checkProgress');
  applyPendingSpotlight();

  // Initialize progress view
  updateProgressView();
}

function switchToBreakdown() {
  closeMenu(); // Close menu when view is selected
  window.scrollTo(0, 0); // Reset scroll position
  hideAllViews();
  showView('breakdown-mode');
  setActiveViewButton('breakdown-btn');
  isCompareMode = false;
  isSankeyMode = false;
  isProgressViewMode = false;
  isRecurringCalendarMode = false;
  isExpenseTrackerMode = false;
  isAchievementsMode = false;
  isSettingsMode = false;
  isBreakdownMode = true;
  updateFloatingButton();

  // Initialize breakdown view
  refreshBreakdown();
}

async function switchToRecurringCalendar() {
  closeMenu(); // Close menu when view is selected
  window.scrollTo(0, 0); // Reset scroll position
  hideAllViews();
  showView('recurring-calendar-mode');
  setActiveViewButton('recurring-calendar-btn');
  isCompareMode = false;
  isSankeyMode = false;
  isProgressViewMode = false;
  isRecurringCalendarMode = true;
  isExpenseTrackerMode = false;
  isAchievementsMode = false;
  updateFloatingButton();
  
  // Initialize recurring calendar
  await initializeRecurringCalendar();
}

function switchToExpenseTracker() {
  closeMenu(); // Close menu when view is selected
  window.scrollTo(0, 0); // Reset scroll position
  hideAllViews();
  showView('expense-tracker-mode');
  setActiveViewButton('expense-tracker-btn');
  isCompareMode = false;
  isSankeyMode = false;
  isProgressViewMode = false;
  isRecurringCalendarMode = false;
  isExpenseTrackerMode = true;
  isAchievementsMode = false;
  updateFloatingButton();
  
  // Apply any pending spotlight
  applyPendingSpotlight();

  // Update budget name in title
  const budgetNameSpan = document.getElementById('expense-journal-budget-name');
  if (budgetNameSpan) {
    const name = getCurrentVersionName();
    budgetNameSpan.textContent = name ? `| ${name}` : '';
  }

  // Initialize expense tracker
  updateExpenseTracker();
}

function showExpenseJournalBudgetPicker(event) {
  const existing = document.getElementById('expense-journal-budget-picker');
  if (existing) { existing.remove(); return; }

  const versions = getBudgetVersions();
  const current = getCurrentVersionName();
  if (versions.length <= 1) return;

  const btn = event.currentTarget;
  const rect = btn.getBoundingClientRect();

  const menu = document.createElement('div');
  menu.id = 'expense-journal-budget-picker';
  menu.className = 'add-budget-menu';
  menu.style.top = `${rect.bottom + 4}px`;
  menu.style.left = `${rect.left}px`;

  menu.innerHTML = versions.map(v => `
    <button class="add-budget-menu-item ${v.name === current ? 'add-budget-menu-item--active' : ''}"
      onclick="loadBudgetVersion(${JSON.stringify(v.name).replace(/"/g, '&quot;')}, false);
               document.getElementById('expense-journal-budget-name').textContent='| '+${JSON.stringify(v.name).replace(/"/g, '&quot;')};
               updateExpenseTracker();
               document.getElementById('expense-journal-budget-picker')?.remove()">
      <span class="add-budget-menu-text">
        <span class="add-budget-menu-label">${v.name}</span>
      </span>
      ${v.name === current ? '<span style="margin-left:auto;color:#667eea">✓</span>' : ''}
    </button>
  `).join('');

  document.body.appendChild(menu);

  setTimeout(() => {
    document.addEventListener('click', function close(e) {
      if (!menu.contains(e.target)) {
        menu.remove();
        document.removeEventListener('click', close);
      }
    });
  }, 0);
}

function switchToAchievements() {
  closeMenu(); // Close menu when view is selected
  window.scrollTo(0, 0); // Reset scroll position
  hideAllViews();
  showView('achievements-mode');
  setActiveViewButton('achievements-btn');
  isCompareMode = false;
  isSankeyMode = false;
  isProgressViewMode = false;
  isRecurringCalendarMode = false;
  isExpenseTrackerMode = false;
  isSettingsMode = false;
  isAchievementsMode = true;
  updateFloatingButton();
  
  // Initialize achievements view
  initializeAchievementsView();
}

function switchToSettingsView() {
  closeMenu(); // Close menu when view is selected
  window.scrollTo(0, 0); // Reset scroll position
  switchToSettings(); // Call the settings module function
  setActiveViewButton('settings-btn');
  isCompareMode = false;
  isSankeyMode = false;
  isProgressViewMode = false;
  isRecurringCalendarMode = false;
  isExpenseTrackerMode = false;
  isAchievementsMode = false;
  isSettingsMode = true;
  updateFloatingButton();
}

/**
 * Set default view on app startup based on user preferences
 */
function setDefaultViewOnStartup() {
  try {
    // Check if settings are loaded first
    import('./modules/settings.js').then(async settingsModule => {
      const currentSettings = settingsModule.getCurrentSettings();
      
      if (!currentSettings) {
        setTimeout(() => setDefaultViewOnStartup(), 200);
        return;
      }
      
      // Now get the default view with properly loaded settings
      const defaultView = settingsModule.getDefaultView();
      
      // Check if budget data is loaded
      const currentBudget = getCurrentBudgetData();
      const budgetVersions = getBudgetVersions();
      const hasBudgetData = currentBudget && currentBudget.categories && Object.keys(currentBudget.categories).length > 0;
      const isNewUser = !budgetVersions || Object.keys(budgetVersions).length === 0;
      
      // If budget data hasn't loaded yet (functions return null), wait and retry
      if (currentBudget === null || budgetVersions === null) {
        setTimeout(() => setDefaultViewOnStartup(), 200);
        return;
      }
      
      // For completely new users (no budget data AND no budget versions), show welcome screen
      if (isNewUser && !hasBudgetData) {
        switchToSingleView();
        return;
      }
      
      // If we don't have budget data but want to go to expenses, wait and retry
      if (!hasBudgetData && defaultView === 'expenses') {
        setTimeout(() => {
          const retryBudget = getCurrentBudgetData();
          const retryHasBudgetData = retryBudget && retryBudget.categories && Object.keys(retryBudget.categories).length > 0;
          
          if (retryHasBudgetData) {
            switchToExpenseTracker();
          } else {
            switchToSingleView();
          }
        }, 300);
        return;
      }
      
      // For non-expense views or when we have budget data, switch immediately
      switchToView(defaultView);
    });
    
  } catch (error) {
    console.error('Error setting default view:', error);
    switchToSingleView(); // Fallback to budget view
  }
}

function switchToView(viewName) {
  switch (viewName) {
    case 'budget':
      switchToSingleView();
      break;
    case 'progress':
      switchToProgressView();
      break;
    case 'expenses':
      switchToExpenseTracker();
      break;
    default:
      switchToSingleView(); // Fallback to budget view
  }
}

/**
 * Navigate to home/default page (for logo clicks)
 */
function navigateToHome() {
  console.log('Navigating to home/default page');
  
  // Close menu immediately to prevent visual confusion
  closeMenu();
  
  // Check if user is completely new (no budget versions AND no data)
  const budgetVersions = getBudgetVersions();
  const currentBudget = getCurrentBudgetData();
  const hasBudgetData = currentBudget && currentBudget.categories && Object.keys(currentBudget.categories).length > 0;
  const isTrulyNewUser = (!budgetVersions || Object.keys(budgetVersions).length === 0) && !hasBudgetData;
  
  // For truly new users, always show budget view to display welcome screen
  if (isTrulyNewUser) {
    console.log('Truly new user detected in navigateToHome, showing budget view with welcome screen');
    switchToSingleView();
    return;
  }
  
  const defaultView = getDefaultView();
  
  switch (defaultView) {
    case 'budget':
      switchToSingleView();
      break;
    case 'progress':
      switchToProgressView();
      break;
    case 'expenses':
      // Check if we have budget data for expense tracker
      if (hasBudgetData) {
        switchToExpenseTracker();
      } else {
        switchToSingleView(); // Fallback if no budget data
      }
      break;
    default:
      switchToSingleView(); // Fallback to budget view
  }
}

/**
 * Helper functions for view management
 */
function hideAllViews() {
  const views = [
    'single-mode',
    'compare-mode',
    'sankey-mode',
    'progress-view-mode',
    'recurring-calendar-mode',
    'expense-tracker-mode',
    'achievements-mode',
    'settings-mode',
    'breakdown-mode'
  ];

  views.forEach(viewId => {
    const viewEl = document.getElementById(viewId);
    if (viewEl) {
      viewEl.style.display = 'none';
    }
  });
}

function showView(viewId) {
  const viewEl = document.getElementById(viewId);
  if (viewEl) {
    if (viewId === 'expense-tracker-mode') {
      viewEl.style.display = 'flex';
      viewEl.style.flexDirection = 'column';
    } else {
      viewEl.style.display = 'block';
    }
  }
}

function setActiveViewButton(buttonId) {
  // Remove active class from all view buttons
  const viewButtons = document.querySelectorAll('.header-btn');
  viewButtons.forEach(btn => btn.classList.remove('active'));
  
  // Add active class to current button
  const activeBtn = document.getElementById(buttonId);
  if (activeBtn) {
    activeBtn.classList.add('active');
  }
}

/**
 * Create and manage floating add expense button
 * Show on mobile for all views except expense tracker
 */
function createFloatingButton() {
  // Check if button already exists
  if (document.getElementById('floating-add-expense-btn')) {
    return;
  }
  
  // Create the floating button element
  const floatingBtn = document.createElement('button');
  floatingBtn.id = 'floating-add-expense-btn';
  floatingBtn.className = 'floating-add-btn';
  floatingBtn.onclick = () => switchToExpenseTracker();
  
  // Create inner elements
  const icon = document.createElement('span');
  icon.className = 'floating-btn-icon';
  icon.textContent = '+';
  
  const text = document.createElement('span');
  text.className = 'floating-btn-text';
  text.textContent = 'Log It';
  
  floatingBtn.appendChild(icon);
  floatingBtn.appendChild(text);
  
  // Insert before the main container to ensure proper stacking context
  const mainContainer = document.getElementById('main-container');
  if (mainContainer) {
    document.body.insertBefore(floatingBtn, mainContainer);
    console.log('Floating button inserted before main container');
  } else {
    // Fallback: append to body
    document.body.appendChild(floatingBtn);
    console.log('Floating button appended to body (main container not found)');
  }
}

function updateFloatingButton() {
  // Ensure button exists
  createFloatingButton();
  
  const floatingBtn = document.getElementById('floating-add-expense-btn');
  if (floatingBtn) {
    // Hide button if currently on expense tracker view
    if (isExpenseTrackerMode) {
      document.body.classList.add('expense-tracker-active');
    } else {
      document.body.classList.remove('expense-tracker-active');
    }
  }
}

/**
 * Placeholder functions for views that need implementation
 * These would be moved to separate view modules in a complete refactor
 */
async function updateComparisonView() {
  console.log('Updating comparison view...');
  try {
    await updateComparison();
  } catch (error) {
    console.error('Failed to update comparison view:', error);
  }
}

async function renderSankeyChart() {
  console.log('Rendering Sankey chart...');
  try {
    await initializeVisualizations();
    await renderSankeyDiagram();
  } catch (error) {
    console.error('Failed to render Sankey chart:', error);
  }
}

async function updateProgressView() {
  try {
    // Clear any existing chart content first
    const container = document.getElementById('budget-vs-actual-container');
    if (container) {
      container.innerHTML = '<div style="padding: 20px; text-align: center;">Loading progress data...</div>';
    }
    
    // Add a small delay to ensure the expense data is available
    // This helps when switching immediately after adding expenses
    await new Promise(resolve => setTimeout(resolve, 100));
    
    await renderBudgetVsActualChart('budget-vs-actual-container');

    // Initialize historical trends chart
    initializeHistoricalTrends();

    // Initialize spending comparison chart with controls
    initializeSpendingComparison();
  } catch (error) {
    console.error('Failed to render budget vs actual chart:', error);
  }
}

// Global function to refresh progress data across all modules
async function refreshAllProgressData() {
  console.log('Refreshing all progress data across modules...');
  try {
    // Refresh progress view if it's currently active
    if (isProgressViewMode) {
      await updateProgressView();
    }
    
    // Could add other module refreshes here in the future
    // if (isExpenseTrackerMode) { await updateExpenseTracker(); }
    
  } catch (error) {
    console.error('Failed to refresh progress data:', error);
  }
}

async function updateExpenseTracker() {
  console.log('Initializing expense tracker view...');
  try {
    await initializeExpenseTracker();

    // Initialize expense history section
    initializeExpenseTrackerHistory();
  } catch (error) {
    console.error('Failed to initialize expense tracker:', error);
  }
}

async function refreshBreakdown() {
  console.log('Generating budget breakdown...');

  const loadingEl = document.getElementById('breakdown-loading');
  const contentEl = document.getElementById('breakdown-content');
  const subtitleEl = document.getElementById('breakdown-subtitle');

  try {
    // Show loading state
    if (loadingEl) loadingEl.style.display = 'block';
    if (contentEl) contentEl.style.display = 'none';

    // Generate breakdown summary
    const summary = await generateBreakdownSummary();

    // Update subtitle with date range
    const dateOptions = { month: 'long', day: 'numeric', year: 'numeric' };
    const monthName = summary.generatedAt.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (subtitleEl) {
      subtitleEl.textContent = `${monthName} • Generated ${summary.generatedAt.toLocaleDateString('en-US', dateOptions)}`;
    }

    // Update budget health card
    const healthCard = document.getElementById('breakdown-health-card');
    const healthIcon = document.getElementById('health-icon');
    const healthStatus = document.getElementById('health-status');
    const healthMessage = document.getElementById('health-message');

    if (healthCard && summary.overall.budgetHealth) {
      healthCard.className = `breakdown-health-card ${summary.overall.budgetHealth.class}`;
      if (healthIcon) healthIcon.textContent = summary.overall.budgetHealth.emoji;
      if (healthStatus) healthStatus.textContent = summary.overall.budgetHealth.status;
      if (healthMessage) healthMessage.textContent = summary.overall.budgetHealth.message;
    }

    // Update metrics
    const metricSpent = document.getElementById('metric-spent');
    const metricSpentDetail = document.getElementById('metric-spent-detail');
    const metricRemaining = document.getElementById('metric-remaining');
    const metricRemainingDetail = document.getElementById('metric-remaining-detail');
    const metricPace = document.getElementById('metric-pace');
    const metricPaceDetail = document.getElementById('metric-pace-detail');
    const metricRecommended = document.getElementById('metric-recommended');

    if (metricSpent) metricSpent.textContent = breakdownFormatCurrency(summary.overall.totalSpent);
    if (metricSpentDetail) metricSpentDetail.textContent = `${Math.round(summary.overall.percentSpent)}% of budget`;
    if (metricRemaining) metricRemaining.textContent = breakdownFormatCurrency(summary.overall.remaining);
    if (metricRemainingDetail) metricRemainingDetail.textContent = `${summary.overall.daysRemaining} days left`;
    if (metricPace) metricPace.textContent = breakdownFormatCurrency(summary.pace.avgDailySpend);
    if (metricPaceDetail) metricPaceDetail.textContent = `Projected: ${breakdownFormatCurrency(summary.pace.projectedTotal)}`;
    if (metricRecommended) metricRecommended.textContent = breakdownFormatCurrency(summary.pace.recommendedDailySpend);

    // Update top categories
    const topCategoriesList = document.getElementById('top-categories-list');
    if (topCategoriesList && summary.categories.top.length > 0) {
      topCategoriesList.innerHTML = summary.categories.top.map(cat => {
        const percentUsed = cat.budgeted > 0 ? (cat.spent / cat.budgeted) * 100 : 0;
        return `
          <div class="category-item">
            <div class="category-info">
              <div class="category-name">${cat.categoryName}</div>
              <div class="category-progress">${Math.round(percentUsed)}% of ${breakdownFormatCurrency(cat.budgeted)}</div>
            </div>
            <div class="category-amount">${breakdownFormatCurrency(cat.spent)}</div>
          </div>
        `;
      }).join('');
    } else if (topCategoriesList) {
      topCategoriesList.innerHTML = '<p class="empty-state">No expenses yet this month</p>';
    }

    // Update warnings
    const warningsSection = document.getElementById('warnings-section');
    const warningsList = document.getElementById('warnings-list');
    const hasWarnings = summary.categories.overBudget.length > 0 || summary.categories.warnings.length > 0;

    if (warningsSection) {
      warningsSection.style.display = hasWarnings ? 'block' : 'none';
    }

    if (warningsList && hasWarnings) {
      const warnings = [
        ...summary.categories.overBudget.map(cat => ({
          type: 'danger',
          icon: '🚨',
          title: `${cat.name} is over budget`,
          detail: `${breakdownFormatCurrency(cat.overage)} over • ${Math.round(cat.percentUsed)}% spent`
        })),
        ...summary.categories.warnings.map(cat => ({
          type: 'warning',
          icon: '⚠️',
          title: `${cat.name} is approaching limit`,
          detail: `${breakdownFormatCurrency(cat.remaining)} remaining • ${Math.round(cat.percentUsed)}% spent`
        }))
      ];

      warningsList.innerHTML = warnings.map(warning => `
        <div class="warning-item ${warning.type}">
          <div class="warning-icon">${warning.icon}</div>
          <div class="warning-content">
            <div class="warning-title">${warning.title}</div>
            <div class="warning-detail">${warning.detail}</div>
          </div>
        </div>
      `).join('');
    }

    // Update largest expenses
    const largestExpensesList = document.getElementById('largest-expenses-list');
    if (largestExpensesList && summary.expenses.largest.length > 0) {
      largestExpensesList.innerHTML = summary.expenses.largest.map(expense => {
        let dateStr = 'No date';
        if (expense.date && expense.date instanceof Date && !isNaN(expense.date)) {
          dateStr = expense.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
        return `
          <div class="expense-item ${expense.isUnplanned ? 'unplanned' : ''}">
            <div class="expense-info">
              <div class="expense-description">${expense.description}</div>
              <div class="expense-meta">
                <span>${expense.categoryName}</span>
                <span>•</span>
                <span>${dateStr}</span>
                ${expense.isUnplanned ? '<span>• Unplanned</span>' : ''}
              </div>
            </div>
            <div class="expense-amount">${breakdownFormatCurrency(expense.amount)}</div>
          </div>
        `;
      }).join('');
    } else if (largestExpensesList) {
      largestExpensesList.innerHTML = '<p class="empty-state">No expenses yet this month</p>';
    }

    // Update unplanned expenses
    const unplannedSection = document.getElementById('unplanned-section');
    const unplannedExpensesList = document.getElementById('unplanned-expenses-list');
    const unplannedTotalValue = document.getElementById('unplanned-total-value');

    if (unplannedSection) {
      unplannedSection.style.display = summary.expenses.unplannedCount > 0 ? 'block' : 'none';
    }

    if (unplannedExpensesList && summary.expenses.unplanned.length > 0) {
      unplannedExpensesList.innerHTML = summary.expenses.unplanned.map(expense => {
        let dateStr = 'No date';
        if (expense.date && expense.date instanceof Date && !isNaN(expense.date)) {
          dateStr = expense.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
        return `
          <div class="expense-item unplanned">
            <div class="expense-info">
              <div class="expense-description">${expense.description}</div>
              <div class="expense-meta">
                <span>${expense.categoryName}</span>
                <span>•</span>
                <span>${dateStr}</span>
              </div>
            </div>
            <div class="expense-amount">${breakdownFormatCurrency(expense.amount)}</div>
          </div>
        `;
      }).join('');
    }

    if (unplannedTotalValue) {
      unplannedTotalValue.textContent = breakdownFormatCurrency(summary.expenses.unplannedTotal);
    }

    // Update upcoming recurring
    const recurringSection = document.getElementById('recurring-section');
    const recurringList = document.getElementById('recurring-expenses-list');
    const recurringTotalValue = document.getElementById('recurring-total-value');

    if (recurringSection) {
      recurringSection.style.display = summary.recurring.count > 0 ? 'block' : 'none';
    }

    if (recurringList && summary.recurring.upcoming.length > 0) {
      recurringList.innerHTML = summary.recurring.upcoming.map(expense => {
        const dateStr = expense.dueDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        return `
          <div class="recurring-item">
            <div class="recurring-info">
              <div class="recurring-name">${expense.name}</div>
              <div class="recurring-meta">${expense.categoryName} • ${dateStr}</div>
            </div>
            <div class="recurring-amount">${breakdownFormatCurrency(expense.amount)}</div>
          </div>
        `;
      }).join('');
    }

    if (recurringTotalValue) {
      recurringTotalValue.textContent = breakdownFormatCurrency(summary.recurring.total);
    }

    // Hide loading, show content
    if (loadingEl) loadingEl.style.display = 'none';
    if (contentEl) contentEl.style.display = 'block';

  } catch (error) {
    console.error('Failed to generate breakdown:', error);

    // Show error state
    if (loadingEl) loadingEl.style.display = 'none';
    if (contentEl) {
      contentEl.style.display = 'block';
      contentEl.innerHTML = `
        <div style="text-align: center; padding: 40px; color: var(--text-light);">
          <p>Unable to generate breakdown. Please try again.</p>
          <p style="font-size: var(--font-sm); margin-top: 10px;">${error.message}</p>
        </div>
      `;
    }
  }
}

/**
 * Export functions for global access (for HTML onclick handlers)
 */
if (typeof window !== 'undefined') {
  // View switching
  window.switchToSingleView = switchToSingleView;
  window.switchToCompareView = switchToCompareView;
  window.switchToSankeyView = switchToSankeyView;
  window.switchToProgressView = switchToProgressView;
  window.switchToRecurringCalendar = switchToRecurringCalendar;
  window.switchToExpenseTracker = switchToExpenseTracker;
  window.showExpenseJournalBudgetPicker = showExpenseJournalBudgetPicker;
  window.updateExpenseTracker = updateExpenseTracker;
  window.switchToAchievements = switchToAchievements;
  window.switchToBreakdown = switchToBreakdown;
  window.refreshBreakdown = refreshBreakdown;
  window.switchToSettings = switchToSettingsView;
  window.navigateToHome = navigateToHome;
  
  // Progress data refresh function
  window.refreshAllProgressData = refreshAllProgressData;
  
  // Menu functions
  window.toggleMenu = toggleMenu;
  window.closeMenu = closeMenu;
  
  // Auth functions
  window.signOut = signOut;
  window.showPasswordResetModal = showPasswordResetModal;
  window.closePasswordResetModal = closePasswordResetModal;
  window.sendPasswordReset = handlePasswordResetSubmit;

  // Getting started checklist
  window.initializeGettingStarted = initializeGettingStarted;
  window.markGettingStartedStep = markGettingStartedStep;
  window.closeGettingStarted = closeGettingStarted;
  window.dismissGettingStarted = dismissGettingStarted;
  window.showGettingStarted = showGettingStarted;
  window.resetGettingStarted = resetGettingStarted;
  window.setPendingSpotlight = setPendingSpotlight;

  // Budget functions
  window.saveCurrentBudget = saveCurrentBudget;
  window.saveCurrentVersion = saveCurrentVersion;
  window.loadBudgetVersion = loadBudgetVersion;
  window.calculateTotals = calculateTotals;
  window.renameCurrentVersion = renameCurrentVersion;
  window.initializeBudgetNameEditing = initializeBudgetNameEditing;
  window.initializeInlineEditing = initializeInlineEditing;
  window.toggleVersionsPanel = toggleVersionsPanel;
  window.handleBudgetManagerBackdropClick = handleBudgetManagerBackdropClick;
  window.filterBudgetVersions = filterBudgetVersions;
  window.setDefaultVersion = setDefaultVersion;
  window.toggleDefaultVersion = toggleDefaultVersion;
  window.duplicateVersion = duplicateVersion;
  window.deleteVersion = deleteVersion;
  window.addIncomeItem = addIncomeItem;
  window.addExpenseCategory = addExpenseCategory;
  window.addItemToCategory = addItemToCategory;
  window.removeItem = removeItem;
  window.removeCategory = removeCategory;
  window.importBudgetFromFile = importBudgetFromFile;
  
  // Link sharing functions
  window.showLinkCodeModal = showLinkCodeModal;
  window.showLinkRedemptionModal = showLinkRedemptionModal;
  window.validateCodeInputs = validateCodeInputs;
  window.checkForFollowers = checkForFollowers;
  window.handleShareAccessClick = handleShareAccessClick;
  window.updateShareAccessButton = updateShareAccessButton;
  window.unlinkHousehold = unlinkHousehold;
  
  // Recurring expense functions
  window.toggleRecurringPanel = toggleRecurringPanel;
  window.saveRecurringConfig = saveRecurringConfig;
  window.cancelRecurringConfig = cancelRecurringConfig;
  window.getRecurringExpenses = getRecurringExpenses;
  window.generateAllRecurringExpenses = generateAllRecurringExpenses;
  window.getCurrentMonthRecurringExpenses = getCurrentMonthRecurringExpenses;
  window.getUpcomingRecurringExpenses = getUpcomingRecurringExpenses;
  window.testRecurringGeneration = testRecurringGeneration;
  window.navigateCalendar = navigateCalendar;
  window.renderCalendar = renderCalendar;
  
  // Auto-logging functions
  window.processRecurringExpenses = processRecurringExpenses;
  window.manualProcessRecurringExpenses = manualProcessRecurringExpenses;
  window.getRecurringExpenseStatus = getRecurringExpenseStatus;
  
  // Income frequency functions
  window.toggleIncomeFrequencyPanel = toggleIncomeFrequencyPanel;
  window.saveIncomeFrequencyConfig = saveIncomeFrequencyConfig;
  window.cancelIncomeFrequencyConfig = cancelIncomeFrequencyConfig;
  
  // Income tax functions
  window.toggleIncomeTaxPanel = toggleIncomeTaxPanel;
  window.saveIncomeTaxConfig = saveIncomeTaxConfig;
  window.cancelIncomeTaxConfig = cancelIncomeTaxConfig;
  
  // Income timing functions
  window.toggleIncomeTimingPanel = toggleIncomeTimingPanel;
  window.saveIncomeTimingConfig = saveIncomeTimingConfig;
  window.cancelIncomeTimingConfig = cancelIncomeTimingConfig;
  
  // Expense tracker functions
  window.initializeExpenseTracker = initializeExpenseTracker;
  window.updateOverallProgress = updateOverallProgress;
  window.deleteExpense = deleteExpense;
  
  // Expense tracker history functions
  window.editExpenseFromTrackerHistory = editExpenseFromTrackerHistory;
  window.deleteExpenseFromTrackerHistory = deleteExpenseFromTrackerHistory;
  window.saveExpenseEdit = saveExpenseEdit;
  window.cancelExpenseEdit = cancelExpenseEdit;
  window.clearExpenseTrackerHistoryFilter = clearExpenseTrackerHistoryFilter;
  window.filterExpenseTrackerHistoryByCategory = filterExpenseTrackerHistoryByCategory;
  window.insertAmountOperator = insertAmountOperator;
  window.deleteAmountChar = deleteAmountChar;
  
  // Visualization functions
  window.initializeVisualizations = initializeVisualizations;
  window.renderSankeyDiagram = renderSankeyDiagram;
  window.resizeChart = resizeChart;
  window.renderBudgetVsActualChart = renderBudgetVsActualChart;
  window.initializeHistoricalTrends = initializeHistoricalTrends;
  window.switchBarChartMode = switchBarChartMode;
  window.updateFlowSummaryCards = updateFlowSummaryCards;
  
  
  // Budget helper functions
  window.getCurrentBudgetData = getCurrentBudgetData;
  window.getCurrentBudgetId = getCurrentBudgetId;
  window.getCurrentVersionName = getCurrentVersionName;
  window.getBudgetVersions = getBudgetVersions;
  
  // Category target functions
  window.openTargetModal = openTargetModal;
  window.closeTargetModal = closeTargetModal;
  window.saveTargetFromModal = saveTargetFromModal;
  window.updateModalInputRow = updateModalInputRow;
  
  // Comparison functions
  window.updateComparison = updateComparison;
  window.refreshComparison = refreshComparison;

  // Income management functions
  window.openIncomeWizard = openIncomeWizard;
  window.closeIncomeWizard = closeIncomeWizard;
  
  // Floating button management
  window.createFloatingButton = createFloatingButton;
  window.updateFloatingButton = updateFloatingButton;

  // Subscription management functions
  window.redirectToCheckout = redirectToCheckout;
  window.openBillingPortal = openBillingPortal;

  // Bank True-Up functions
  window.openBankTrueUpModal = openBankTrueUpModal;
  window.closeBankTrueUpModal = closeBankTrueUpModal;
  window.bankTrueUpNextStep = bankTrueUpNextStep;
  window.bankTrueUpPrevStep = bankTrueUpPrevStep;
  window.bankTrueUpAddTransaction = bankTrueUpAddTransaction;
  window.showTrueUpTab = showTrueUpTab;
  window.resumeBankTrueUpModal = resumeBankTrueUpModal;
}

// Page tour (used by all page help buttons)
window.startPageTour = startPageTour;

/**
 * Initialize the app when DOM is ready
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeApp();
  });
} else {
  initializeApp();
}

export { initializeApp };